import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { toastr } from 'react-redux-toastr';
import { push, replace } from 'react-router-redux';
import { PricingActions } from '~/actions';
import type { IAppState } from '~/appstate';
import type { IMetaProduct, IProductWithUM } from '~/domain';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { Customer } from '~/helpers';
import { default as route, default as Routes } from '~/helpers/Routes';
import type {
	GenerateGroupingRecommendationResult,
	MetaProductGroupingRemove,
	ProductWithUM,
} from '~/ogm-hub';
import * as Hub from '~/ogm-hub';
import {
	MetaProductControllerV4,
	MetaProductGroupingSource,
	MetaProductGroupingStatus,
	MetaProductType,
	UmSelectionMode,
} from '~/ogm-hub';
import type { SelectionRuleSet } from '~/remote/hubs/Web_Controllers_V2_CustomersController';
import { fetchReferencedTags } from '~/Slices/Rearrange/DynamicRearrangeSlice';
import { tagIdsOnLinks } from '~/helpers/tagLinks';
import * as MetaProductPageSlice from '~/Slices/MetaProductPage/MetaProductPageSlice';
import { IMaintainedByType, IMetaProductStatus } from '~/Slices/schema';
import type { IProductHeader } from '~/values';

export interface AddGroupingRemoveAction {
	sourceMetaProductId: number;
	targetGroupingId: number;
	// could eventually be expanded to include UM for splits
	supplierProductNumber: string;
}

export interface RemoveGroupingRemoveAction {
	targetGroupingId: number;
	// could eventually be expanded to include UM for splits
	supplierProductNumber: string;
}

export interface GroupingRenameAction {
	targetGroupingId: number;
	name: string;
}

export interface IGroupingState {
	targetId: number;
	id?: number;
	metaProductId?: number;
	name: string;
	isNameDirty: boolean;
	status: MetaProductGroupingStatus;
	removes: Record<number, MetaProductGroupingRemove>;
}

export type IMetaProductLookup = Record<number, IMetaProduct>;

export type IDistributionCenterCoverageLookup = Record<
	string,
	Hub.DistributionCenter
>;

export interface IRearrangeState {
	isSelectByUm: boolean;
	umSelectionModeId: UmSelectionMode;
	peerGroupingSource: MetaProductGroupingSource;
	metaProducts: {
		sourceMetaProductIds: number[];
		metaProductLookup: IMetaProductLookup;
	};
	distributionCenterCoverage: {
		latestRequest: number;
		isLoading: boolean;
		distributionCenterCoverageLookup: IDistributionCenterCoverageLookup;
	};
	groupings: {
		latestRequest: number;
		isLoading: boolean;
		groupings: IGroupingState[];
		remainingGroupingIdsToReview: number[];
	};
	isSaving: boolean;
	selectionRuleSets: SelectionRuleSet[];
}

const initialState: IRearrangeState = {
	isSelectByUm: false,
	umSelectionModeId: UmSelectionMode.Off,
	peerGroupingSource: MetaProductGroupingSource.User,
	metaProducts: {
		sourceMetaProductIds: [],
		metaProductLookup: {},
	},
	distributionCenterCoverage: {
		latestRequest: null,
		isLoading: false,
		distributionCenterCoverageLookup: {},
	},
	groupings: {
		latestRequest: null,
		isLoading: false,
		groupings: [],
		remainingGroupingIdsToReview: [],
	},
	isSaving: false,
	selectionRuleSets: [],
};

export const getDistributionCenterCoverage = createAsyncThunk<
	{ requestTime: number; distributionCenters: Hub.DistributionCenter[] },
	{ blueprintId: number },
	{ state: IAppState }
>(
	'rearrange/getDistributionCenterCoverage',
	async (args, { rejectWithValue }) => {
		try {
			const requestTime = Date.now();

			const result = await unwrapFetch2(
				Hub.BlueprintControllerV3.GetDistributionCenterInfo(args.blueprintId)
			);

			if (result.errors.length == 0) {
				return { distributionCenters: result.results, requestTime };
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface GetGroupingInformationRequest {
	blueprintId: number;
	inputMetaProductIds: number[];
	existingOutputGroupingIds: number[];
}

export const getGroupingInformation = createAsyncThunk<
	{
		peerGroupingSource: MetaProductGroupingSource;
		existingGroupings: IGroupingState[];
		sourceMetaProductIds: number[];
		metaProducts: Hub.GetMetaProductResponse[];
		requestTime: number;
	},
	GetGroupingInformationRequest,
	{ state: IAppState }
>(
	'rearrange/getGroupingInformation',
	async (args, { rejectWithValue, dispatch }) => {
		try {
			const requestTime = Date.now();

			let existingGroupings: IGroupingState[] = [
				{
					targetId: 0,
					name: '',
					removes: {},
					isNameDirty: false,
					status: MetaProductGroupingStatus.New,
				},
			];
			let peerGroupingSource = MetaProductGroupingSource.User;

			const result = await unwrapFetch2(
				Hub.MetaProductControllerV4.GetMetaProductGroupingInformation({
					blueprintId: args.blueprintId,
					outputGroupingIds: args.existingOutputGroupingIds ?? [],
					sourceMetaProductIds: args.inputMetaProductIds ?? [],
				})
			);

			if (result.metaProductGroupings.length > 0) {
				const draftProducts = await unwrapFetch2(
					Hub.MetaProductControllerV4.GetMetaProductsWithItems({
						blueprintId: args.blueprintId,
						metaProductIds: result.metaProductGroupings.map(
							(grouping) => grouping.groupedMetaProductId
						),
					})
				);
				const nameLookUp = draftProducts.metaProducts.reduce(
					(lookUp, metaproduct) => {
						lookUp[metaproduct.id] = metaproduct.name;
						return lookUp;
					},
					{}
				);

				peerGroupingSource = result.metaProductGroupings[0].source;

				existingGroupings = result.metaProductGroupings.map(
					(grouping, index) => {
						return {
							targetId: index,
							id: grouping.id,
							metaProductId: grouping.groupedMetaProductId,
							name: nameLookUp[grouping.groupedMetaProductId],
							status: grouping.statusId,
							isNameDirty: false,
							removes: grouping.removes.reduce((agg, remove) => {
								agg[remove.sourceMetaProductId] = remove;
								return agg;
							}, {}),
						};
					}
				);
			}

			const removeSourceMetaProductIds: number[] = [];
			result.metaProductGroupings.forEach((grouping) => {
				grouping.removes.forEach((remove) => {
					removeSourceMetaProductIds.push(remove.sourceMetaProductId);
				});
			});
			const existingSourceMetaProductIds = uniq(removeSourceMetaProductIds);

			const sourceMetaProductIds = uniq(
				args.inputMetaProductIds.concat(existingSourceMetaProductIds)
			);

			const foundMetaProductIds = result.metaProducts.reduce(
				(agg: Record<number, boolean>, metaProduct) => {
					agg[metaProduct.id] = true;
					return agg;
				},
				{}
			);

			const foundSourceMetaProductIds = sourceMetaProductIds.reduce(
				(agg: number[], sourceMetaProductId) => {
					if (foundMetaProductIds[sourceMetaProductId]) {
						agg.push(sourceMetaProductId);
					}
					return agg;
				},
				[]
			);

			const supplierProductNumbers: string[] = [];
			result.metaProducts.forEach((metaProduct) => {
				metaProduct.products.forEach((product) => {
					supplierProductNumbers.push(product.supplierProductNumber);
				});
			});
			const allProductNumbers = uniq(supplierProductNumbers);

			dispatch(
				PricingActions.loadMetaProductHeaderPrices(
					args.blueprintId,
					allProductNumbers
				)
			);

			dispatch(
				RearrangeSlice.actions.setSelectionRuleSets(
					Customer.customer().selectionRuleSets
				)
			);

			const hasDynamicProducts = result.metaProducts.some(
				(mp) => mp.metaProductType === MetaProductType.Dynamic
			);
			if (hasDynamicProducts) {
				let allTagIds: number[] = [];
				result.metaProducts.forEach((mp) => {
					allTagIds = uniq(
						allTagIds.concat(tagIdsOnLinks(mp.tagLinks ?? []))
					);
				});
				dispatch(fetchReferencedTags(allTagIds));
			}

			return {
				peerGroupingSource,
				sourceMetaProductIds: foundSourceMetaProductIds,
				metaProducts: result.metaProducts,
				existingGroupings: existingGroupings,
				requestTime,
			};
		} catch (err) {
			toastr.error(
				'Getting Grouping Information Failed',
				'Getting grouping information failed.  Please try again.'
			);

			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const regroup = createAsyncThunk<
	{
		regroupResults: GenerateGroupingRecommendationResult;
		metaProductLookup: Record<string, [number]>;
		requestTime: number;
	},
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('rearrange/regroup', async (_, { getState, rejectWithValue }) => {
	const state = getState();

	try {
		const metaProductIds = state?.rearrange.metaProducts.sourceMetaProductIds;
		const productInfo: {
			metaProductId: number;
			productHeader: IProductHeader;
		}[] = [];
		metaProductIds.forEach((metaProductId) => {
			state.rearrange.metaProducts.metaProductLookup[
				metaProductId
			].productHeaders.forEach((productHeader) => {
				productInfo.push({ metaProductId, productHeader });
			});
		});

		const productIdentifiers: ProductWithUM[] = productInfo.map((product) => ({
			corpSupplierCode: product.productHeader.corpSupplierCode,
			supplierProductNumber: product.productHeader.supplierProductNumber,
			unitMeasureCode: product.productHeader.unitMeasureCodes[0],
			specIndicatorId: null,
		}));

		const uniqProductIdentifiers: ProductWithUM[] = productIdentifiers.reduce(
			(agg, productIdentifier) => {
				const existingItem = agg.find(
					(item) =>
						item.corpSupplierCode === productIdentifier.corpSupplierCode &&
						item.supplierProductNumber ===
							productIdentifier.supplierProductNumber &&
						item.unitMeasureCode === productIdentifier.unitMeasureCode
				);
				if (!existingItem) {
					agg.push(productIdentifier);
				}
				return agg;
			},
			[]
		);

		const requestTime = Date.now();

		const response = await Hub.MetaProductGroupingController.Regroup({
			products: uniqProductIdentifiers,
		});

		const metaProductLookup: Record<string, [number]> = productInfo.reduce(
			(agg, product) => {
				if (
					!agg[
						product.productHeader.supplierProductNumber +
							product.productHeader.unitMeasureCodes[0]
					]
				) {
					agg[
						product.productHeader.supplierProductNumber +
							product.productHeader.unitMeasureCodes[0]
					] = [product.metaProductId];
				} else {
					agg[
						product.productHeader.supplierProductNumber +
							product.productHeader.unitMeasureCodes[0]
					].push(product.metaProductId);
				}
				return agg;
			},
			{}
		);

		if (response.ok) {
			return {
				regroupResults: response.value,
				metaProductLookup,
				requestTime,
			};
		}
	} catch (err) {
		toastr.error(
			'Generating Grouping Recommendations Failed',
			'Generating grouping recommendations failed.  Please try again.'
		);

		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export const saveGroupings = createAsyncThunk<
	{ remainingGroupingIdsToReview: number[] },
	{ blueprintId: number },
	{ state: IAppState }
>(
	'rearrange/saveGroupings',
	async (args, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();

			const groupings = state.rearrange.groupings.groupings;

			const requests = groupings
				.reduce(
					(agg: Hub.UpsertMetaProductGroupingRequest[], grouping, index) => {
						if (grouping.status !== MetaProductGroupingStatus.Staged) {
							const removals = selectMetaProductRemovals(state, index);
							const name = selectOutputMetaProductName(grouping, removals);

							agg.push({
								metaProductGroupingId: grouping.id,
								metaProductGroupingRemoves: Object.keys(grouping.removes).map(
									(key) => grouping.removes[key]
								),
								newMetaProductName: name,
								metaProductType: MetaProductType.Manual,
								tagLinks: [],
								additionalProductNumbers: [],
							});
						}

						return agg;
					},
					[]
				)
				.filter((x) => x.metaProductGroupingRemoves.length !== 0);

			const result = await unwrapFetch2(
				MetaProductControllerV4.UpsertMetaProductGroupings({
					blueprintId: args.blueprintId,
					source: state.rearrange.peerGroupingSource,
					isSelectByUm: state.rearrange.isSelectByUm,
					umSelectionModeId: state.rearrange.umSelectionModeId,
					unitMeasureCodes:
						state.rearrange.umSelectionModeId === UmSelectionMode.EachOnly
							? ['EA']
							: [],
					requests,
				})
			);

			const groupingIdsInSet: number[] = uniq(
				groupings
					.filter((x) => x.id != null)
					.map((x) => x.id)
					.concat(result.responses.map((x) => x.groupingId))
			);

			const remainingGroupingIdsToReview: number[] = uniq(
				result.responses.map((x) => x.groupingId)
			);
			const firstMetaProductIdToReview =
				result.responses[0].groupedMetaProductId;

			dispatch(
				replace({
					pathname: Routes.rearrange(args.blueprintId),
					query: { gid: groupingIdsInSet },
				})
			);
			dispatch(
				push(
					route.prioritize(
						args.blueprintId.toString(),
						firstMetaProductIdToReview
					)
				)
			);

			return {
				remainingGroupingIdsToReview: remainingGroupingIdsToReview,
			};
		} catch (err) {
			toastr.error('Saving Failed', 'Saving failed.  Please try again.');

			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const RearrangeSlice = createSlice({
	name: 'Rearrange',
	initialState,
	reducers: {
		addGrouping: (state) => {
			state.groupings.groupings.push({
				targetId: state.groupings.groupings.length,
				name: '',
				removes: {},
				isNameDirty: false,
				status: MetaProductGroupingStatus.New,
			});
		},
		addGroupingRemove: (
			state,
			action: PayloadAction<AddGroupingRemoveAction>
		) => {
			const { targetGroupingId, sourceMetaProductId, supplierProductNumber } =
				action.payload;

			if (state.groupings.groupings[targetGroupingId]) {
				if (
					!state.groupings.groupings[targetGroupingId].removes[
						sourceMetaProductId
					]
				) {
					state.groupings.groupings[targetGroupingId].removes[
						sourceMetaProductId
					] = {
						sourceMetaProductId,
						productNumbers: [supplierProductNumber],
					};
				} else {
					const existingProductNumbers =
						state.groupings.groupings[targetGroupingId].removes[
							sourceMetaProductId
						].productNumbers;
					state.groupings.groupings[targetGroupingId].removes[
						sourceMetaProductId
					].productNumbers = uniq(
						existingProductNumbers.concat([supplierProductNumber])
					);
				}
			}
		},
		removeGroupingRemove: (
			state,
			action: PayloadAction<RemoveGroupingRemoveAction>
		) => {
			const { targetGroupingId, supplierProductNumber } = action.payload;

			const toDelete: string[] = [];
			if (state.groupings.groupings[targetGroupingId] != null) {
				Object.keys(
					state.groupings.groupings[targetGroupingId].removes
				).forEach((sourceMetaProductId) => {
					const newProductNumbers = state.groupings.groupings[
						targetGroupingId
					].removes[sourceMetaProductId].productNumbers.filter(
						(x) => x !== supplierProductNumber
					);
					if (newProductNumbers.length === 0) {
						toDelete.push(sourceMetaProductId);
					} else {
						state.groupings.groupings[targetGroupingId].removes[
							sourceMetaProductId
						].productNumbers = newProductNumbers;
					}
				});
			}

			toDelete.forEach((sourceMetaProductId) => {
				delete state.groupings.groupings[targetGroupingId].removes[
					sourceMetaProductId
				];
			});
		},
		renameGrouping: (state, action: PayloadAction<GroupingRenameAction>) => {
			const { targetGroupingId, name } = action.payload;

			if (state.groupings.groupings[targetGroupingId]) {
				const grouping = state.groupings.groupings[targetGroupingId];
				grouping.name = name;
				grouping.isNameDirty = true;
			}
		},
		setRemainingGroupingIdsToReview: (
			state,
			action: PayloadAction<number[]>
		) => {
			state.groupings.remainingGroupingIdsToReview = action.payload;
		},
		setSelectionRuleSets: (state, action) => {
			state.selectionRuleSets = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getGroupingInformation.pending, (state) => {
			state.isSaving = false;
			state.metaProducts = initialState.metaProducts;
			state.groupings = {
				...initialState.groupings,
				isLoading: true,
			};
			state.isSelectByUm = initialState.isSelectByUm;
			state.peerGroupingSource = initialState.peerGroupingSource;
			state.distributionCenterCoverage =
				initialState.distributionCenterCoverage;
		});
		builder.addCase(getGroupingInformation.fulfilled, (state, action) => {
			if (state.groupings.latestRequest <= action.payload.requestTime) {
				state.groupings.isLoading = false;
				state.groupings.groupings = action.payload.existingGroupings;
				state.metaProducts.sourceMetaProductIds =
					action.payload.sourceMetaProductIds;
				state.peerGroupingSource = action.payload.peerGroupingSource;

				state.metaProducts.metaProductLookup =
					action.payload.metaProducts.reduce(
						(agg: IMetaProductLookup, metaProduct) => {
							// the old model makes a lot of assumptions and casts from any to this type
							// it would be nice to get rid of this eventually and use the actual API type
							// but it's very bound up within rearrange and its subcomponent assumptions
							agg[metaProduct.id] = metaProduct as any;
							return agg;
						},
						{}
					);

				const isSelectByUm =
					action.payload.metaProducts.length > 0
						? action.payload.metaProducts[0].isSelectByUm
						: false;
				state.isSelectByUm = isSelectByUm;

				const umSelectionModeId =
					action.payload.metaProducts.length > 0
						? action.payload.metaProducts[0].umSelectionModeId
						: UmSelectionMode.Off;
				state.umSelectionModeId = umSelectionModeId;

				const hasUmMismatch = action.payload.metaProducts.some(
					(mp) => mp.umSelectionModeId !== umSelectionModeId
				);
				if (hasUmMismatch) {
					toastr.error(
						'UM Mode Mismatch',
						'Cannot group master items with different UM selection modes.'
					);
				}
				state.groupings.latestRequest = action.payload.requestTime;
			}
		});
		builder.addCase(getGroupingInformation.rejected, (state) => {
			state.groupings.isLoading = false;
		});
		builder.addCase(getDistributionCenterCoverage.pending, (state) => {
			state.distributionCenterCoverage.isLoading = true;
			state.distributionCenterCoverage.distributionCenterCoverageLookup =
				initialState.distributionCenterCoverage.distributionCenterCoverageLookup;
		});
		builder.addCase(
			getDistributionCenterCoverage.fulfilled,
			(state, action) => {
				if (
					action.payload.requestTime >=
					state.distributionCenterCoverage.latestRequest
				) {
					state.distributionCenterCoverage.isLoading = false;
					state.distributionCenterCoverage.latestRequest =
						action.payload.requestTime;
					state.distributionCenterCoverage.distributionCenterCoverageLookup =
						action.payload.distributionCenters.reduce(
							(
								agg: IDistributionCenterCoverageLookup,
								distributionCenter: any
							) => {
								agg[distributionCenter.dssiCode] = distributionCenter;
								return agg;
							},
							{}
						);
				}
			}
		);
		builder.addCase(saveGroupings.pending, (state) => {
			state.isSaving = true;
		});
		builder.addCase(saveGroupings.fulfilled, (state, action) => {
			state.isSaving = false;
			state.groupings.remainingGroupingIdsToReview =
				action.payload.remainingGroupingIdsToReview;
		});
		builder.addCase(saveGroupings.rejected, (state) => {
			state.isSaving = false;
		});
		builder.addCase(regroup.pending, (state) => {
			state.isSaving = false;
			state.groupings.isLoading = true;
		});
		builder.addCase(regroup.fulfilled, (state, action) => {
			if (state.groupings.latestRequest <= action.payload.requestTime) {
				state.groupings.groupings =
					action.payload.regroupResults.productGroupings.map((group, index) => {
						const removes = group.products.reduce((agg, product) => {
							const sourceMetaProductIds =
								action.payload.metaProductLookup[
									product.supplierProductNumber + product.unitOfMeasure
								];

							sourceMetaProductIds.forEach((mpId) => {
								if (!agg[mpId]) {
									agg[mpId] = {
										sourceMetaProductId: mpId,
										productNumbers: [product.supplierProductNumber],
									};
								} else {
									agg[mpId].productNumbers.push(product.supplierProductNumber);
								}
							});

							return agg;
						}, {});

						return {
							targetId: index,
							name: '',
							removes: removes,
							isNameDirty: false,
							status: MetaProductGroupingStatus.New,
						};
					});
				state.groupings.isLoading = false;
				state.groupings.latestRequest = action.payload.requestTime;
			}
		});
		builder.addCase(regroup.rejected, (state) => {
			state.groupings.isLoading = false;
		});
	},
});

export const selectOutputGroupings = (state: IAppState): IGroupingState[] => {
	return state.rearrange.groupings.groupings.filter(
		(x) => x.status !== MetaProductGroupingStatus.Staged
	);
};

export const selectStagedGroupings = (state: IAppState): IGroupingState[] => {
	return state.rearrange.groupings.groupings.filter(
		(x) => x.status === MetaProductGroupingStatus.Staged
	);
};

export const selectInputMetaProduct = (
	state: IAppState,
	id: number
): IMetaProduct => {
	// split um could be factored in here eventually
	const removedProductNumbers: string[] = [];
	state.rearrange.groupings.groupings.forEach((grouping) => {
		const numbers = grouping.removes[id]?.productNumbers ?? [];
		numbers.forEach((productNumber) => {
			removedProductNumbers.push(productNumber);
		});
	});
	const removedMetaProductItemsLookup = removedProductNumbers.reduce(
		(agg, removedProductNumber: string) => {
			agg[removedProductNumber] = true;
			return agg;
		},
		{} as Record<string, boolean>
	);

	const metaProduct = { ...state.rearrange.metaProducts.metaProductLookup[id] };
	const existing = metaProduct.products
		? metaProduct.products.map((product) => {
				return {
					...product,
					exists: !removedMetaProductItemsLookup[product.supplierProductNumber],
				};
		  })
		: [];

	return {
		...metaProduct,
		products: existing,
	};
};

export const selectOutputMetaProductName = (
	grouping: IGroupingState,
	removals: { productHeaders: IProductHeader[]; productUms: IProductWithUM[] }[]
): string => {
	const nameIsDefined = (grouping.name?.trim() ?? '') != '';
	const defaultName =
		removals[0]?.productHeaders[0]?.extendedDescription ??
		removals[0]?.productHeaders[0]?.description;

	const name = nameIsDefined ? grouping.name : defaultName;
	return name;
};

export const selectMetaProductRemovals = (
	state: IAppState,
	targetId: number
): {
	productHeaders: IProductHeader[];
	productUms: IProductWithUM[];
}[] => {
	// split um could be factored in here eventually
	const grouping = state.rearrange.groupings.groupings[targetId];
	const pickedProductNumbers = {};
	const removals: {
		productHeaders: IProductHeader[];
		productUms: IProductWithUM[];
	}[] = [];
	Object.keys(grouping.removes).forEach((sourceMetaProductId) => {
		const relatedMetaProduct =
			state.rearrange.metaProducts.metaProductLookup[sourceMetaProductId];
		const removedNumbers =
			grouping.removes[sourceMetaProductId]?.productNumbers ?? [];

		removedNumbers.forEach((removedProductNumber) => {
			if (pickedProductNumbers[removedProductNumber]) {
				return;
			}

			const relatedProductHeaders = relatedMetaProduct.productHeaders.filter(
				(x) => x.supplierProductNumber === removedProductNumber
			);
			const relatedProductUMs = relatedMetaProduct.products.filter(
				(x) => x.supplierProductNumber === removedProductNumber
			);

			if (
				relatedProductHeaders.length === 0 ||
				relatedProductUMs.length === 0
			) {
				return;
			}

			pickedProductNumbers[removedProductNumber] = true;

			removals.push({
				productHeaders: relatedProductHeaders,
				productUms: relatedProductUMs,
			});
		});
	});

	return removals;
};

export function rulesetToFilterOptions(token: string): string[] {
	switch (token) {
		case 'leadtime-sdrn':
			return ['S', 'D', 'R', 'N'];
		case 'usf-leadtime':
			return ['S', 'D', 'R'];
		case 'leadtime-sdr':
			return ['S', 'D', 'R'];
		case 'leadtime-sd':
			return ['S', 'D'];
		case 'priority-sdrn':
			return ['S', 'D', 'R', 'N'];
		case 'priority-sdr':
			return ['S', 'D', 'R'];
		case 'priority-sd':
			return ['S', 'D'];
		case 'only-stocked':
			return ['S'];
		case 'only-remote':
			return ['R'];
		default:
			return [];
	}
}

export const getCoveredDCsForProduct = (
	selectionRuleSets: SelectionRuleSet[],
	product: IProductWithUM,
	productHeader: IProductHeader,
	selectionRuleSetId: number,
	isSelectByUm: boolean
): string[] => {
	if (productHeader == null) {
		return [];
	}

	let coverageFilters: string[] = ['S', 'D', 'R'];
	if (selectionRuleSetId != null) {
		const displayToken = selectionRuleSets?.find(
			(x) => x.id === selectionRuleSetId
		)?.displayToken;
		coverageFilters = rulesetToFilterOptions(displayToken);
	}

	return MetaProductPageSlice.filterDCCoverage(
		product,
		productHeader.coveredDistributionCenters,
		productHeader.remoteStockDcs,
		productHeader.demandStockDcs,
		productHeader.nonstockDcs,
		coverageFilters,
		isSelectByUm
	);
};

export const selectOutputMetaProduct = (
	state: IAppState,
	targetId: number
): IMetaProduct => {
	// split um could be factored in here eventually
	const grouping = state.rearrange.groupings.groupings[targetId];
	const removals = selectMetaProductRemovals(state, targetId);
	const name = selectOutputMetaProductName(grouping, removals);
	const products: IProductWithUM[] = [];
	const productHeaders: IProductHeader[] = [];
	removals.forEach((removal) => {
		products.push(...removal.productUms);
		productHeaders.push(...removal.productHeaders);
	});

	return {
		id: null,
		name: grouping.isNameDirty ? grouping.name : name,
		search: name,
		itemCount: removals.length,
		locationAvailability: 0,
		distributionCenterAvailability: 0,
		products,
		productHeaders,
		maintainedBy: IMaintainedByType.OGM3,
		statusId: IMetaProductStatus.Draft,
		selectionRuleSetId: 2,
		pendingRebuild: false,
		coverageRatio: '',
		isSelectByUm: state.rearrange.isSelectByUm,
		umSelectionModeId: state.rearrange.umSelectionModeId,
		tagLinks: [],
	};
};
